import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { ZeeFinancialFormSchema } from '@/components/forms/schemas/ZeeFinancialFormSchema';
import { ZeeOtherFinancialFormSchema } from '@/components/forms/schemas/ZeeOtherFinancialFormSchema';
import { ZeeFinancialForm } from '@/components/forms/zee/ZeeFinancialForm';
import { ZeeOtherFinancialForm } from '@/components/forms/zee/ZeeOtherFinancialForm';
import { Button } from '@/components/ui/button';
import { useCreateLeadFinancial } from '@/hooks/leads/useCreateLeadFinancial';
import { useGetLeadFinancial } from '@/hooks/leads/useGetLeadFinancial';
import { useUpdateLeadFinancial } from '@/hooks/leads/useUpdateLeadFinancial';
import { toast } from '@/hooks/ui/use-toast';
import useLeadStore from '@/stores/useLeadStore.ts';

type LeadFinancialFormValues = z.infer<typeof ZeeFinancialFormSchema>;
type LeadOtherFinancialFormValues = z.infer<typeof ZeeOtherFinancialFormSchema>;

const LeadFinancialTab = () => {
  const { id: leadId } = useParams<{ id: string }>();

  const financialForm = useForm<LeadFinancialFormValues>({
    resolver: zodResolver(ZeeFinancialFormSchema),
    defaultValues: {},
  });

  const otherFinancialForm = useForm<LeadOtherFinancialFormValues>({
    resolver: zodResolver(ZeeOtherFinancialFormSchema),
    defaultValues: {},
  });

  const {
    candidateFinancial,
    candidateOtherFinancial,
    setCandidateFinancial,
    setCandidateOtherFinancial,
  } = useLeadStore();

  const {
    result: candidateFinancialResult,
    isLoading: isCandidateFinancialLoading,
    isError: isCandidateFinancialError,
    isSuccess: isCandidateFinancialSuccess,
  } = useGetLeadFinancial(leadId || '');

  const {
    mutate: updateCandidateFinancial,
    result: updateCandidateFinancialResult,
    isError: isUpdateCandidateFinancialError,
    isSuccess: isUpdateCandidateFinancialSuccess,
    isLoading: isUpdateCandidateFinancialLoading,
  } = useUpdateLeadFinancial();

  const {
    mutate: createCandidateFinancial,
    result: createCandidateFinancialResult,
    isError: isCreateCandidateFinancialError,
    isSuccess: isCreateCandidateFinancialSuccess,
    isLoading: isCreateCandidateFinancialLoading,
  } = useCreateLeadFinancial();

  const resetForms = useCallback(() => {
    financialForm.reset({
      ...candidateFinancial,
    });

    otherFinancialForm.reset({
      ...candidateOtherFinancial,
      net_worth:
        typeof candidateOtherFinancial?.net_worth === 'number'
          ? String(candidateOtherFinancial.net_worth)
          : candidateOtherFinancial?.net_worth,
    });
  }, [
    candidateFinancial,
    candidateOtherFinancial,
    financialForm,
    otherFinancialForm,
  ]);

  const handleSubmit = async (): Promise<void> => {
    const isFinancialFormValid = await financialForm.trigger();
    const isOtherFinancialFormValid = await otherFinancialForm.trigger();

    if (isFinancialFormValid && isOtherFinancialFormValid) {
      const financialValues = financialForm.getValues();
      const otherFinancialValues = otherFinancialForm.getValues();

      const transformedFinancialValues = {
        ...financialValues,
        spouse_significant_other_name:
          financialValues.spouse_significant_other_name?.trim() || '',
        spouse_significant_other_employment:
          financialValues.spouse_significant_other_employment?.trim() || '',
        spouse_significant_other_annual_compensation:
          financialValues.spouse_significant_other_annual_compensation ?? 0,
        spouse_significant_other_phone:
          financialValues.spouse_significant_other_phone?.trim() || '',
        household_income: financialValues.household_income?.trim() || null,
        income_levels: financialValues.income_levels?.trim() || null,
        premium_income_household:
          financialValues.premium_income_household?.trim() || null,
        company_name: financialValues.company_name?.trim() || null,
        company_domain: financialValues.company_domain?.trim() || null,
        primary_industry: financialValues.primary_industry?.trim() || null,
        seniority_level: financialValues.seniority_level?.trim() || null,
        job_title: financialValues.job_title?.trim() || null,
      };

      const transformedOtherFinancialValues = {
        cash_or_equivalent_assets:
          otherFinancialValues.cash_or_equivalent_assets ?? 0,
        savings_or_certificates:
          otherFinancialValues.savings_or_certificates ?? 0,
        stocks_or_bonds_or_securities:
          otherFinancialValues.stocks_or_bonds_or_securities ?? 0,
        retirement_plan_or_ira:
          otherFinancialValues.retirement_plan_or_ira ?? 0,
        home_market_value: otherFinancialValues.home_market_value ?? 0,
        other_real_estate: otherFinancialValues.other_real_estate ?? 0,
        autos: otherFinancialValues.autos ?? 0,
        other_assets: otherFinancialValues.other_assets ?? 0,
        other_assets_description:
          otherFinancialValues.other_assets_description?.trim() || null,
        total_assets: otherFinancialValues.total_assets ?? 0,
        home_mortgage_balance: otherFinancialValues.home_mortgage_balance ?? 0,
        other_real_estate_debt:
          otherFinancialValues.other_real_estate_debt ?? 0,
        auto_loans: otherFinancialValues.auto_loans ?? 0,
        other_debt: otherFinancialValues.other_debt ?? 0,
        other_debt_description:
          otherFinancialValues.other_debt_description?.trim() || null,
        total_liabilities: otherFinancialValues.total_liabilities ?? 0,
        net_worth: otherFinancialValues.net_worth?.trim() || null,
        home_purchased_years_ago:
          otherFinancialValues.home_purchased_years_ago?.trim() || null,
        owns_investment: otherFinancialValues.owns_investment || null,
      };

      const submitPayload = {
        ...transformedFinancialValues,
        other_financial: transformedOtherFinancialValues,
        id: leadId || '',
      };

      if (!candidateFinancial && !candidateOtherFinancial) {
        createCandidateFinancial(leadId || '', submitPayload);
      } else {
        updateCandidateFinancial(leadId || '', submitPayload);
      }
    }
  };

  const renderEditButtons = () => {
    return (
      <div className="flex w-full justify-end">
        <div className="flex gap-1">
          <Button size="sm" className="w-full md:w-fit" onClick={handleSubmit}>
            Save Changes
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isCandidateFinancialError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isCandidateFinancialError]);

  useEffect(() => {
    if (isCandidateFinancialSuccess && candidateFinancialResult?.data) {
      if (candidateFinancialResult?.data?.zee_financial) {
        setCandidateFinancial(candidateFinancialResult?.data?.zee_financial);
      }

      if (candidateFinancialResult?.data?.zee_other_financial) {
        setCandidateOtherFinancial(
          candidateFinancialResult?.data?.zee_other_financial
        );
      }

      resetForms();
    }
  }, [
    candidateFinancialResult,
    isCandidateFinancialSuccess,
    resetForms,
    setCandidateFinancial,
    setCandidateOtherFinancial,
  ]);

  useEffect(() => {
    if (isUpdateCandidateFinancialError || isCreateCandidateFinancialError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isUpdateCandidateFinancialError, isCreateCandidateFinancialError]);

  useEffect(() => {
    if (
      isCreateCandidateFinancialSuccess ||
      isUpdateCandidateFinancialSuccess
    ) {
      if (createCandidateFinancialResult?.data) {
        setCandidateFinancial(
          createCandidateFinancialResult?.data?.zee_financial
        );
        setCandidateOtherFinancial(
          createCandidateFinancialResult?.data?.zee_other_financial
        );
        resetForms();
      }
      if (updateCandidateFinancialResult?.data) {
        setCandidateFinancial(
          updateCandidateFinancialResult?.data?.zee_financial
        );
        setCandidateOtherFinancial(
          updateCandidateFinancialResult?.data?.zee_other_financial
        );
        resetForms();
      }
    }
  }, [
    createCandidateFinancialResult,
    updateCandidateFinancialResult,
    isCreateCandidateFinancialSuccess,
    isUpdateCandidateFinancialSuccess,
    resetForms,
    setCandidateFinancial,
    setCandidateOtherFinancial,
  ]);

  if (isCandidateFinancialLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      {(isCreateCandidateFinancialLoading ||
        isUpdateCandidateFinancialLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <ZeeFinancialForm form={financialForm} />
      <ZeeOtherFinancialForm form={otherFinancialForm} />
      {renderEditButtons()}
    </div>
  );
};

export default LeadFinancialTab;
